import { useState, useEffect, useRef } from 'react';
import UploadScreen from './components/UploadScreen';
import AdminDashboard from './components/AdminDashboard';
import HubPortal from './components/HubPortal';
import ManagerDashboard from './components/ManagerDashboard';
import { parseXLSX } from './lib/parser';
import {
  createUpload,
  insertHubData,
  insertTransactions,
  getLatestUpload,
  getLatestHubData,
  uploadExcelFile,
  updateUploadFileUrl,
  getHubUserById,
} from './lib/supabase';

// ─── PIN Modal ────────────────────────────────────────────────────────────────
function PinModal({ onSuccess, onClose }) {
  const [pin, setPin]       = useState('');
  const [error, setError]   = useState(false);
  const [shake, setShake]   = useState(false);
  const inputRef            = useRef();

  useEffect(() => { inputRef.current?.focus(); }, []);

  function handleSubmit(e) {
    e.preventDefault();
    const adminPin = process.env.REACT_APP_ADMIN_PIN || 'wheeley2025';
    if (pin === adminPin) {
      localStorage.setItem('wh_admin', 'true');
      onSuccess();
    } else {
      setError(true);
      setShake(true);
      setPin('');
      setTimeout(() => setShake(false), 500);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#000000dd',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div style={{
        background: '#1a1f2e', border: '1px solid #2a2f3e', borderRadius: 16,
        padding: 32, width: 320, fontFamily: "'DM Mono', monospace",
        animation: shake ? 'shake .4s ease' : 'none',
      }}>
        <style>{`
          @keyframes shake {
            0%,100% { transform: translateX(0); }
            20%,60%  { transform: translateX(-8px); }
            40%,80%  { transform: translateX(8px); }
          }
        `}</style>

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🔐</div>
          <div style={{ color: '#f59e0b', fontWeight: 700, fontSize: 13, letterSpacing: 3 }}>ADMIN ACCESS</div>
          <div style={{ color: '#4b5563', fontSize: 11, marginTop: 4 }}>Enter your PIN to continue</div>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input
            ref={inputRef}
            type="password"
            placeholder="PIN"
            value={pin}
            onChange={e => { setPin(e.target.value); setError(false); }}
            style={{
              background: '#0f1117', color: '#f3f4f6',
              border: `1px solid ${error ? '#f87171' : '#374151'}`,
              borderRadius: 8, padding: '10px 14px', fontSize: 16,
              fontFamily: 'inherit', textAlign: 'center', letterSpacing: 4,
              outline: 'none',
            }}
          />
          {error && (
            <div style={{ color: '#f87171', fontSize: 11, textAlign: 'center' }}>
              Incorrect PIN. Try again.
            </div>
          )}
          <button type="submit" style={{
            background: '#f59e0b', color: '#0f1117', border: 'none',
            borderRadius: 8, padding: '10px', fontWeight: 900,
            fontSize: 12, letterSpacing: 2, cursor: 'pointer', fontFamily: 'inherit',
          }}>
            UNLOCK
          </button>
          <button type="button" onClick={onClose} style={{
            background: 'transparent', color: '#4b5563', border: 'none',
            fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
          }}>
            Cancel
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [page,          setPage]          = useState('loading');
  const [hubData,       setHubData]       = useState([]);
  const [uploadMeta,    setUploadMeta]    = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [showPin,       setShowPin]       = useState(false);

  // Admin state — persisted in localStorage so it survives tab closes and browser restarts
  const [isAdmin, setIsAdmin] = useState(() => localStorage.getItem('wh_admin') === 'true');
  const [adminSection, setAdminSection] = useState('payment'); // 'cod' | 'payment'

  // Hub user state — persisted in localStorage so it survives browser restarts
  const [hubUser, setHubUser] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('wh_hub_user') || 'null');
      if (u) u.role = u.role || 'hub'; // normalize sessions stored before role was added
      return u;
    }
    catch { return null; }
  });

  function handleHubLogin(user) {
    const normalizedUser = { ...user, role: user.role || 'hub' };
    setHubUser(normalizedUser);
    try { localStorage.setItem('wh_hub_user', JSON.stringify(normalizedUser)); }
    catch (e) { console.warn('Could not persist session to localStorage:', e); }
  }

  function handleHubLogout() {
    setHubUser(null);
    localStorage.removeItem('wh_hub_user');
  }

  useEffect(() => { init(); }, []); // eslint-disable-line

  // Returns true if the hub list looks like real data (not a parse-failed "Unknown" dump)
  function hubsLookValid(hubs) {
    if (!hubs || hubs.length === 0) return false;
    const validCount = hubs.filter(h => h.hub && h.hub !== 'Unknown').length;
    return validCount > 0;
  }

  async function init() {
    try {
      // Get the latest upload, then try hub_data scoped to it.
      // If the latest upload has no hub_data (e.g. a failed partial upload created
      // an upload record but insertHubData never ran successfully), OR if all hubs
      // parsed as "Unknown" (column header mismatch in the Excel file), fall back to
      // v_latest_pendency so the dashboard shows the last intact upload instead of ₹0.
      const latestUpload = await getLatestUpload();
      let latestHubs     = await getLatestHubData(latestUpload?.id);
      if (!hubsLookValid(latestHubs)) {
        latestHubs = await getLatestHubData(); // fallback: v_latest_pendency
      }
      if (latestHubs.length > 0) {
        setUploadMeta(latestUpload || null);
        setHubData(latestHubs);
      }

      // Re-fetch hub user session from DB so that any admin changes to managed_hubs,
      // role, hub_name, or is_active are immediately picked up without requiring a re-login.
      const cachedUser = hubUser; // read from useState (populated from localStorage)
      if (cachedUser?.id) {
        try {
          const freshUser = await getHubUserById(cachedUser.id);
          if (freshUser && freshUser.is_active) {
            const normalizedUser = { ...freshUser, role: freshUser.role || 'hub' };
            setHubUser(normalizedUser);
            localStorage.setItem('wh_hub_user', JSON.stringify(normalizedUser));
          } else {
            // User was deactivated or deleted — force logout
            setHubUser(null);
            localStorage.removeItem('wh_hub_user');
          }
        } catch (e) {
          console.warn('Could not refresh hub user session from DB:', e);
          // Non-fatal: continue with the cached session so the user isn't locked out
        }
      }

      // Restore the right page based on who is already logged in.
      // isAdmin is read from localStorage so it's available synchronously here.
      const adminActive = localStorage.getItem('wh_admin') === 'true';
      setPage(adminActive ? 'admin' : 'hub');
    } catch (e) {
      console.error('Init error:', e);
      setPage('hub');
    }
  }

  async function handleUpload(buffer, filename) {
    setUploadLoading(true);
    try {
      const { hubs, transactions } = parseXLSX(buffer);
      const upload = await createUpload(filename);

      uploadExcelFile(buffer, filename, upload.id)
        .then(url => updateUploadFileUrl(upload.id, url))
        .catch(e => console.warn('Excel file storage skipped (non-critical):', e.message));

      await insertHubData(upload.id, hubs);
      await insertTransactions(upload.id, transactions);
      setUploadMeta(upload);
      setHubData(hubs.map(h => ({
        ...h,
        upload_id:         upload.id,
        total_diff:        h.totalDiff,
        total_to_collect:  h.totalToCollect,
        total_collected:   h.totalCollected,
        total_deposited:   h.totalDeposited,
        open_to_collect:   h.openToCollect,
        open_deposited:    h.openDeposited,
        open_diff:         h.openDiff,
        total_submitted:   0,
        pendency:          h.totalDiff,
        sub_count:         0,
      })));
      setPage('admin');
    } catch (e) {
      alert('Upload failed: ' + e.message);
      console.error(e);
    }
    setUploadLoading(false);
  }

  async function handleRefresh() {
    try {
      const latestUpload = await getLatestUpload();
      // Prefer hub_data scoped to the latest upload.
      // If that upload's data was deleted, fall back to v_latest_pendency
      // which returns the most recent hub_data from any previous upload,
      // so the dashboard shows the last intact upload rather than ₹0.
      let latestHubs = latestUpload ? await getLatestHubData(latestUpload.id) : [];
      if (!hubsLookValid(latestHubs)) {
        latestHubs = await getLatestHubData(); // fallback: v_latest_pendency
      }
      if (latestHubs.length > 0) {
        setUploadMeta(latestUpload || null);
        setHubData(latestHubs);
      } else {
        setUploadMeta(null);
        setHubData([]);
        setPage(isAdmin ? 'upload' : 'hub');
      }
    } catch (e) { console.error(e); }
  }

  function handleAdminUnlock() {
    setIsAdmin(true);
    setShowPin(false);
    setPage('admin');
  }

  function handleAdminLogout() {
    localStorage.removeItem('wh_admin');
    setIsAdmin(false);
    setPage('hub');
  }

  const navBtn = (label, target) => (
    <button
      onClick={() => setPage(target)}
      style={{
        padding: '8px 18px',
        borderRadius: 8,
        border: '1px solid',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 1,
        cursor: 'pointer',
        fontFamily: "'DM Mono', monospace",
        borderColor: page === target ? '#f59e0b' : '#2a2f3e',
        background:  page === target ? '#f59e0b' : '#1a1f2e',
        color:       page === target ? '#0f1117' : '#9ca3af',
        transition: 'all .2s',
      }}>
      {label}
    </button>
  );

  if (page === 'loading') {
    return (
      <div style={{ minHeight: '100vh', background: '#0b0e14', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b', fontFamily: "'DM Mono', monospace" }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'DM Mono', monospace" }}>

      {/* ── Navigation bar (top-right) ──────────────────────────────────────── */}
      {/* Hidden when any user is logged in — they are locked to their portal/dashboard */}
      {!hubUser && (
        <div style={{ position: 'fixed', top: 16, right: 24, zIndex: 1000, display: 'flex', gap: 8, alignItems: 'center' }}>

          {/* Admin tabs + HUB PORTAL only visible after admin unlock */}
          {isAdmin && navBtn('ADMIN', 'admin')}
          {isAdmin && adminSection === 'cod' && navBtn('+ UPLOAD', 'upload')}
          {isAdmin && navBtn('HUB PORTAL', 'hub')}

          {/* Lock icon: 🔓 to exit admin, 🔐 to enter — always visible so admin can unlock */}
          {isAdmin ? (
            <button
              onClick={handleAdminLogout}
              title="Exit admin mode"
              style={{
                background: '#1a1f2e', border: '1px solid #2a2f3e',
                borderRadius: 8, width: 36, height: 36,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontSize: 16,
              }}
            >
              🔓
            </button>
          ) : (
            <button
              onClick={() => setShowPin(true)}
              title="Admin login"
              style={{
                background: '#1a1f2e', border: '1px solid #2a2f3e',
                borderRadius: 8, width: 36, height: 36,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontSize: 16,
              }}
            >
              🔐
            </button>
          )}
        </div>
      )}

      {/* ── Pages ─────────────────────────────────────────────────────────── */}
      {/* Admin pages are blocked when a hub user is logged in */}
      {page === 'upload' && (isAdmin || hubUser?.role === 'control_tower') && (
        <UploadScreen onUpload={handleUpload} loading={uploadLoading} />
      )}
      {page === 'admin' && isAdmin && !hubUser && (
        <AdminDashboard
          hubData={hubData}
          uploadMeta={uploadMeta}
          onNewUpload={() => setPage('upload')}
          onRefresh={handleRefresh}
          section={adminSection}
          onSectionChange={setAdminSection}
          fullAdmin={true}
        />
      )}
      {/* Control Tower: shown when a control_tower user is logged in — same dashboard as
          Admin, but with fullAdmin=false so Excel export / Lock All / Unlock All / Delete
          Upload are hidden, and Control Tower accounts can't be managed from the Users tab. */}
      {hubUser?.role === 'control_tower' && (
        <AdminDashboard
          hubData={hubData}
          uploadMeta={uploadMeta}
          onNewUpload={() => setPage('upload')}
          onRefresh={handleRefresh}
          section={adminSection}
          onSectionChange={setAdminSection}
          fullAdmin={false}
          hubUser={hubUser}
          onLogout={handleHubLogout}
        />
      )}
      {/* Manager Dashboard: shown when a manager is logged in */}
      {hubUser?.role === 'manager' && (
        <ManagerDashboard
          user={hubUser}
          onLogout={handleHubLogout}
        />
      )}
      {/* Hub Portal: shown when on hub page, OR when regular hub user is logged in, OR when not admin */}
      {(!hubUser || hubUser.role === 'hub') && (page === 'hub' || hubUser || (!isAdmin && page !== 'hub')) && (
        <HubPortal
          hubData={hubData}
          uploadMeta={uploadMeta}
          user={hubUser}
          onLogin={handleHubLogin}
          onLogout={handleHubLogout}
        />
      )}

      {/* ── PIN modal ─────────────────────────────────────────────────────── */}
      {showPin && (
        <PinModal
          onSuccess={handleAdminUnlock}
          onClose={() => setShowPin(false)}
        />
      )}
    </div>
  );
}
